import {DateUtils, TranslateUtils, rawRenderingLib} from '@wallaby-ui/core';
import {parseISO} from 'date-fns';
import React, {useCallback, useMemo, useRef} from 'react';
import {useIntl} from 'react-intl';
import {useStore} from 'react-redux';

export type FilterConfig = {
  name: string;
  ui: {
    label: string;
    placeholder?: string;
  };
};
export type UnlabeledFilterConfig = {
  name: string;
};
export type NumberRangeFilterConfig = {
  name: string;
  ui: {
    label: string;
    min?: number;
    max?: number;
  };
};

export type Formatters = {
  t: (key: string) => string;
  tEnum: (enumName: string | undefined, enumValueName: string | undefined) => string;
  parseDate: (isoString: string | undefined) => Date;
  formatDateTime: (date: string) => string;
  formatDate: (date: string) => string;
  formatDateTimeShort: (date: string) => string;
  formatDateShort: (date: string) => string;
  /** Format float up to maxPrecision = 1, formatFloat(1.15) == "1.2", formatFloat(2) == "2" */
  formatFloat: (float: number | undefined | null, maxPrecision?: number) => string | undefined;
  /** Format float with exact precision, formatFloatExact(1.15) == "1.2", formatFloatExact(2) == "2.0" */
  formatFloatExact: (float: number | undefined | null, precision?: number) => string | undefined;
  formatStrings(format: string, values: Record<string, string>): string[];
  formatString(format: string, values: Record<string, string>): string;
  formatText: (format: string, values: Record<string, string | JSX.Element>) => JSX.Element;
};
export function useFormatters(): Formatters {
  // stateRef
  const intl = useIntl();
  const store = useStore();
  const stateRef = useRef({
    intl,
    store,
  });
  stateRef.current.intl = intl;
  stateRef.current.store = store;
  // formatters
  const t = useCallback((key: string) => TranslateUtils.translate(key, stateRef.current.intl), []);
  const tEnum = useCallback((enumName: string | undefined, enumValueName: string | undefined) => {
    return (
      (enumName &&
        enumValueName &&
        rawRenderingLib.translatedEnumValueRenderer(
          {enumName, enumValueName},
          {intl: stateRef.current.intl, store: stateRef.current.store}
        )) ??
      ''
    );
  }, []);
  const parseDate = useCallback((isoString: string | undefined) => {
    return parseISO(isoString ?? '');
  }, []);
  const formatDateTime = useCallback((date: string) => {
    return DateUtils.getFormattedDate(parseISO(date ?? ''), stateRef.current.intl, false, false) ?? '';
  }, []);
  const formatDate = useCallback((date: string) => {
    return DateUtils.getFormattedDate(parseISO(date ?? ''), stateRef.current.intl, true, false) ?? '';
  }, []);
  const formatDateTimeShort = useCallback(
    (date: string) => DateUtils.getFormattedDate(parseISO(date ?? ''), stateRef.current.intl, false, true) ?? '',
    []
  );
  const formatDateShort = useCallback(
    (date: string) => DateUtils.getFormattedDate(parseISO(date ?? ''), stateRef.current.intl, true, true) ?? '',
    []
  );
  const formatters: Formatters = useMemo(
    () => ({
      t,
      tEnum,
      parseDate,
      formatDateTime,
      formatDate,
      formatDateTimeShort,
      formatDateShort,
      formatFloat,
      formatFloatExact,
      formatStrings,
      formatString,
      formatText,
    }),
    [t, tEnum, parseDate, formatDateTime, formatDate, formatDateTimeShort, formatDateShort]
  );
  return formatters;
}
function formatFloat(float: number | undefined | null, maxPrecision: number = 1): string | undefined {
  if (float == null) return undefined;
  const fixedPrecision = float.toFixed(maxPrecision);
  if (maxPrecision === 0) return fixedPrecision;
  const indexOfDecimalPoint = fixedPrecision.length - maxPrecision - 1;
  let j: number;
  for (j = fixedPrecision.length - 1; j >= indexOfDecimalPoint && (fixedPrecision[j] === '0' || fixedPrecision[j] === '.'); j--) {
    // noop
  }
  const zerosTrimmed = fixedPrecision.slice(0, j + 1);
  return zerosTrimmed.replace('.', ','); // TODO: should this be locale dependent?
}
function formatFloatExact(float: number | undefined | null, precision: number = 1) {
  if (float == null) return undefined;
  return float.toFixed(precision).replace('.', ','); // TODO: should this be locale dependent?
}
function formatStrings(format: string, values: Record<string, string>): string[] {
  return formatBy(format, values, (_, value) => value);
}
function formatString(format: string, values: Record<string, string>): string {
  return formatStrings(format, values).join('');
}
function formatText(format: string, values: Record<string, string | JSX.Element>): JSX.Element {
  return (
    <>
      {formatBy(format, values, (key, value) => {
        if (value?.constructor?.name === 'String') {
          return <span key={key}>{value}</span>;
        }
        return React.cloneElement(value as JSX.Element, {
          'key': `format.${key}`,
          'data-name': `format.${key}`,
        });
      })}
    </>
  );
}
function formatBy<V, O>(format: string, values: Record<string, V>, mapTo: (key: number | string, value: string | V) => O): O[] {
  const accOutputs: O[] = [];
  let i = 0;
  Array.from(format.matchAll(/{([^{}]+)}/g)).forEach((match) => {
    const start = match.index ?? i;
    const end = start + match[0].length;
    if (start !== i) {
      accOutputs.push(mapTo(i, format.slice(i, start)));
    }
    const key = match[1];
    if (key in values) {
      accOutputs.push(mapTo(key, values[key]));
    } else {
      accOutputs.push(mapTo(start, format.slice(start, end)));
    }
    i = end;
  });
  if (i !== format.length) {
    accOutputs.push(mapTo(i, format.slice(i)));
  }
  return accOutputs;
}
